// Canonical helpers for person-form fields used across all forms.
// Field order (everywhere a person is captured):
//   1. Gender, 2. First Name, 3. Surname, 4. DoB, 5. Email,
//   6. Phone (with country dial-code), 7. Address,
//   complementary: Postal code, City, Country (synced with phone country).
//
// KEEP IN SYNC with Backend/utils/personFields.js — the server applies the
// same formatting rules on save, so a rule changed here must change there too.

// Backend / DB value for the "Not Communicated" gender uses an underscore.
export const GENDER_NOT_COMMUNICATED = 'Not_Communicated';

// Shared input limits — one convention for every form.
export const EMAIL_MAX_LENGTH = 254;   // RFC 5321 total length
export const PHONE_MAX_LENGTH = 15;    // longest national numbering plan
export const POSTAL_CODE_MAX_LENGTH = 10;

// Format check for a filled-in email (length limits are separate).
export const EMAIL_PATTERN = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Build the canonical gender options list. Pass a `t` function (react-i18next).
// Tries multiple translation keys so we work in any form's namespace; falls back
// to English defaults so callers never see a blank label.
export const getGenderOptions = (t) => ([
    {
        value: 'male',
        label: t('common.gender.male', { defaultValue: t('doctorRegister.male', { defaultValue: 'Male' }) }),
    },
    {
        value: 'female',
        label: t('common.gender.female', { defaultValue: t('doctorRegister.female', { defaultValue: 'Female' }) }),
    },
    {
        value: GENDER_NOT_COMMUNICATED,
        label: t('common.gender.notCommunicated', { defaultValue: 'Not Communicated' }),
    },
    {
        value: 'other',
        label: t('common.gender.other', { defaultValue: t('doctorRegister.other', { defaultValue: 'Other' }) }),
    },
]);

// First Name: only the first character uppercased; the rest as-typed.
export const formatFirstName = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
};

// Surname (family name): same convention as the first name — first character
// uppercased, the rest as-typed. Replaces the earlier fully-uppercased rule,
// which made surnames shout everywhere they were displayed.
export const formatSurname = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
};

// Email: stored and compared lowercase; trim stray whitespace as typed/pasted.
export const normalizeEmail = (value) => {
    if (!value) return value;
    return value.trim().toLowerCase();
};

// Quick shape check for a filled email (callers still enforce their own
// required/length messages).
export const isValidEmail = (value) => EMAIL_PATTERN.test((value || '').trim());

// Phone: digits only, capped at the international max. Country masters may
// narrow the length further (mobileMinLength / mobileMaxLength).
export const sanitizePhone = (value, maxLength = PHONE_MAX_LENGTH) => {
    if (!value) return '';
    return value.replace(/\D/g, '').slice(0, Math.min(maxLength, PHONE_MAX_LENGTH));
};

// Postal / PIN code: letters and digits only, capped at 10 — the shared rule
// for every form. Country masters may narrow the length (zipMin / zipMax).
export const sanitizePostalCode = (value) => {
    if (!value) return '';
    return value.replace(/[^0-9A-Za-z]/g, '').slice(0, POSTAL_CODE_MAX_LENGTH);
};

// Alphanumeric postal-code pattern honouring a country's min/max length,
// both clamped to the global cap.
export const postalCodePattern = (min = 1, max = POSTAL_CODE_MAX_LENGTH) => {
    const lo = Math.max(1, Math.min(min, POSTAL_CODE_MAX_LENGTH));
    const hi = Math.max(lo, Math.min(max, POSTAL_CODE_MAX_LENGTH));
    return new RegExp(`^[0-9A-Za-z]{${lo},${hi}}$`);
};
